import React, { useState, useEffect } from 'react';
import { TabsContent } from '../../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Textarea } from '../../ui/textarea';
import { Input } from '../../ui/input';
import { Checkbox } from '../../ui/checkbox';
import { Button } from '../../ui/button';
import { Plus, ChevronDown, ChevronUp, Loader2, FileSearch } from 'lucide-react';
import { isCanonicalAsset, resolveSignedAsset } from '../../../lib/signedAssets';

// Canonical /assets sources need a short-lived signed URL before the browser
// can load them; external/data URLs render as-is. Resolving async keeps the
// gallery thumbnails and the preview from showing a broken image.
const SignedImg = ({ src, ...props }) => {
  const [resolved, setResolved] = useState(isCanonicalAsset(src) ? null : src);

  useEffect(() => {
    let active = true;
    if (isCanonicalAsset(src)) {
      setResolved(null);
      resolveSignedAsset(src).then((url) => {
        if (active) setResolved(url || src);
      });
    } else {
      setResolved(src);
    }
    return () => { active = false; };
  }, [src]);

  return <img src={resolved || undefined} {...props} />;
};

const DescripcionTab = ({
  aiResponse,
  loading,
  selectedCommand,
  onCommandChange,
  descripcionCommands,
  showCustomMessage,
  onToggleCustomMessage,
  message,
  onMessageChange,
  hasAnyImages,
  allImages,
  selectedImageIndex,
  onSelectImage,
  describeAllImages,
  onToggleDescribeAll,
  imageUrl,
  onImageUrlChange,
  onAddExternalImage,
  isDraggingImage,
  onDragOver,
  onDragLeave,
  onDrop,
  onSend,
  onInsert,
  onResponseChange,
  onResetResponse
}) => (
  <TabsContent value="descripcion" className="space-y-3">
    {!aiResponse ? (
      <>
        {hasAnyImages ? (
          <>
            <div className="space-y-2">
              <label className="text-sm font-medium">Galería de imágenes</label>
              <div className="grid grid-cols-3 gap-2 p-2 bg-slate-50 dark:bg-slate-800/60 rounded-lg border border-slate-200 dark:border-slate-700 max-h-48 overflow-y-auto">
                {allImages.map((img, i) => (
                  <div
                    key={i}
                    onClick={() => onSelectImage(i)}
                    className={`relative cursor-pointer rounded overflow-hidden border-2 transition-all hover:scale-105 ${
                      selectedImageIndex === i
                        ? 'border-green-500 shadow-md'
                        : 'border-transparent hover:border-slate-300 dark:hover:border-slate-500'
                    }`}
                  >
                    <SignedImg
                      src={img.src}
                      alt={img.alt || `Imagen ${i + 1}`}
                      className="w-full h-20 object-cover"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs px-1 py-0.5 truncate">
                      {i + 1}
                      {img.isExternal && ' 🔗'}
                    </div>
                  </div>
                ))}
              </div>

              <div className="border rounded p-2 bg-white dark:bg-slate-800">
                <SignedImg
                  src={allImages[selectedImageIndex]?.src}
                  alt={allImages[selectedImageIndex]?.alt || `Imagen ${selectedImageIndex + 1}`}
                  className="w-full rounded max-h-40 object-contain"
                />
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 text-center">
                  Imagen {selectedImageIndex + 1} de {allImages.length}
                </p>
              </div>
            </div>

            {allImages.length > 1 && (
              <div className="flex items-center space-x-2 p-2 bg-blue-50 dark:bg-blue-950/40 rounded border border-blue-200 dark:border-blue-800">
                <Checkbox id="describe-all" checked={describeAllImages} onCheckedChange={onToggleDescribeAll} />
                <label
                  htmlFor="describe-all"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                >
                  Describir todas las imágenes ({allImages.length})
                </label>
              </div>
            )}
          </>
        ) : null}

        <div className="space-y-2" onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}>
          <label className="text-sm font-medium">
            {hasAnyImages ? 'Agregar más imágenes' : 'URL de la imagen'}
          </label>
          <div
            className={`flex gap-2 p-3 rounded-lg border-2 border-dashed transition-colors ${
              isDraggingImage ? 'border-green-500 bg-green-50 dark:bg-green-950/40' : 'border-slate-300 dark:border-slate-600'
            }`}
          >
            <Input
              placeholder="https://ejemplo.com/imagen.jpg o arrastra aquí"
              value={imageUrl}
              onChange={(e) => onImageUrlChange(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && onAddExternalImage()}
              disabled={loading}
              className="flex-1"
            />
            <Button
              type="button"
              size="sm"
              onClick={onAddExternalImage}
              disabled={!imageUrl.trim()}
              className="bg-green-600 hover:bg-green-700"
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          {isDraggingImage && <p className="text-xs text-green-600 text-center">Suelta la URL aquí</p>}
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium">Comando de descripción</label>
          <Select value={selectedCommand} onValueChange={onCommandChange}>
            <SelectTrigger>
              <SelectValue placeholder="Selecciona un tipo de descripción..." />
            </SelectTrigger>
            <SelectContent>
              {descripcionCommands.map((cmd) => (
                <SelectItem key={cmd.id} value={cmd.id}>
                  {cmd.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <button
            onClick={onToggleCustomMessage}
            className="flex items-center gap-2 text-sm font-medium hover:text-green-600 transition-colors"
          >
            {showCustomMessage ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
            O escribe instrucción personalizada
          </button>

          {showCustomMessage && (
            <Textarea
              placeholder="Ej: Describe los colores y la composición..."
              value={message}
              onChange={(e) => onMessageChange(e.target.value)}
              rows={3}
              className="resize-none text-sm"
              disabled={loading}
            />
          )}
        </div>

        <Button
          onClick={onSend}
          disabled={loading || !hasAnyImages || (!message && !selectedCommand)}
          className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              {describeAllImages ? 'Describiendo todas...' : 'Describiendo...'}
            </>
          ) : (
            <>
              <FileSearch className="w-4 h-4 mr-2" />
              Describir Imagen{describeAllImages ? 's' : ''}
            </>
          )}
        </Button>
      </>
    ) : (
      <>
        <div className="space-y-2">
          <label className="text-sm font-medium">Descripción (editable)</label>
          <Textarea
            value={aiResponse}
            onChange={(e) => onResponseChange(e.target.value)}
            rows={10}
            className="resize-none text-sm"
          />
        </div>

        <div className="flex gap-2">
          <Button
            onClick={onInsert}
            className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Insertar
          </Button>
        </div>

        <Button variant="outline" onClick={onResetResponse} className="w-full">
          ← Volver a editar
        </Button>
      </>
    )}
  </TabsContent>
);

export default DescripcionTab;
